import { create } from "zustand";
import type { AuthUser } from "./types";
import { fetchCurrentUser, loginWithCredentials, refreshTokens, registerUser } from "./api";
import { clearAllListFilters } from "@/lib/hooks/usePersistedFilters";

const TOKEN_STORAGE_KEY = "auth_tokens";
const REFRESH_LOCK_STORAGE_KEY = "auth_refresh_lock";
const ACCESS_TOKEN_COOKIE_MAX_AGE = 86400;
const REFRESH_LOCK_TTL_MS = 30_000;
const REFRESH_LOCK_POLL_MS = 100;
const refreshLockOwner = `${Date.now()}-${Math.random()}`;

function setAccessTokenCookie(token: string | null) {
  if (token) {
    document.cookie = `access_token=${token}; path=/; max-age=${ACCESS_TOKEN_COOKIE_MAX_AGE}; SameSite=Lax`;
  } else {
    document.cookie = "access_token=; path=/; max-age=0";
  }
}

function persistTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({ accessToken, refreshToken }));
  setAccessTokenCookie(accessToken);
}

function clearPersistedTokens() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  setAccessTokenCookie(null);
}

function getPersistedTokens(): { accessToken: string; refreshToken: string } | null {
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.accessToken && parsed.refreshToken) return parsed;
    return null;
  } catch {
    return null;
  }
}

type RefreshLock = {
  owner: string;
  expiresAt: number;
  refreshToken: string;
};

function readRefreshLock(): RefreshLock | null {
  try {
    const raw = localStorage.getItem(REFRESH_LOCK_STORAGE_KEY);
    if (!raw) return null;
    const lock = JSON.parse(raw);
    if (lock.owner && lock.expiresAt && lock.refreshToken) return lock;
    return null;
  } catch {
    return null;
  }
}

function acquireRefreshLock(refreshToken: string): boolean {
  const now = Date.now();
  const current = readRefreshLock();
  if (
    current &&
    current.owner !== refreshLockOwner &&
    current.expiresAt > now
  ) {
    return false;
  }

  localStorage.setItem(
    REFRESH_LOCK_STORAGE_KEY,
    JSON.stringify({
      owner: refreshLockOwner,
      expiresAt: now + REFRESH_LOCK_TTL_MS,
      refreshToken,
    }),
  );
  return readRefreshLock()?.owner === refreshLockOwner;
}

function releaseRefreshLock() {
  if (readRefreshLock()?.owner === refreshLockOwner) {
    localStorage.removeItem(REFRESH_LOCK_STORAGE_KEY);
  }
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Shared across overlapping refresh() calls. Refresh tokens rotate
// (one-time use), so two concurrent refreshes would race — the loser sends
// an already-spent token and logs the user out. All callers share one.
let refreshInFlight: Promise<boolean> | null = null;

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<boolean>;
  refreshUser: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  refreshToken: null,
  user: null,
  isLoading: false,
  isAuthenticated: false,

  initialize: async () => {
    const { isAuthenticated } = get();
    if (isAuthenticated) return;

    const tokens = getPersistedTokens();
    if (!tokens) return;

    // Always refresh to get the latest claims (e.g. new office assignments)
    try {
      set({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      });
      if (await get().refresh()) return;

      const user = await fetchCurrentUser(tokens.accessToken);
      persistTokens(tokens.accessToken, tokens.refreshToken);
      set({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        user,
        isAuthenticated: true,
      });
    } catch {
      // Refresh failed — fall back to stored access token
      try {
        const user = await fetchCurrentUser(tokens.accessToken);
        setAccessTokenCookie(tokens.accessToken);
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          user,
          isAuthenticated: true,
        });
      } catch {
        clearPersistedTokens();
      }
    }
  },

  login: async (email, password) => {
    set({ isLoading: true });
    try {
      const tokens = await loginWithCredentials(email, password);
      const user = await fetchCurrentUser(tokens.access_token);
      persistTokens(tokens.access_token, tokens.refresh_token);
      set({
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
      throw new Error("Login failed");
    }
  },

  register: async (email, password, name) => {
    set({ isLoading: true });
    try {
      const tokens = await registerUser(email, password, name);
      const user = await fetchCurrentUser(tokens.access_token);
      persistTokens(tokens.access_token, tokens.refresh_token);
      set({
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
      throw new Error("Registration failed");
    }
  },

  logout: () => {
    clearPersistedTokens();
    clearAllListFilters();
    localStorage.removeItem("multi-rater-selected-offices");
    localStorage.removeItem("multi-rater-selected-company");
    window.location.href = "/auth/login";
  },

  refresh: async () => {
    // Single-flight: concurrent callers share one in-flight refresh so a
    // rotated refresh token is never spent twice (see refreshInFlight above).
    if (refreshInFlight) return refreshInFlight;

    const syncPersistedTokens = async (currentRefreshToken: string) => {
      const persisted = getPersistedTokens();
      if (!persisted || persisted.refreshToken === currentRefreshToken) {
        return false;
      }

      try {
        const user = await fetchCurrentUser(persisted.accessToken);
        setAccessTokenCookie(persisted.accessToken);
        set({
          accessToken: persisted.accessToken,
          refreshToken: persisted.refreshToken,
          user,
          isAuthenticated: true,
        });
        return true;
      } catch {
        return false;
      }
    };

    const waitForOtherTabRefresh = async (currentRefreshToken: string) => {
      const deadline = Date.now() + REFRESH_LOCK_TTL_MS;
      while (Date.now() < deadline) {
        if (await syncPersistedTokens(currentRefreshToken)) return true;
        const lock = readRefreshLock();
        if (!lock || lock.owner === refreshLockOwner || lock.expiresAt <= Date.now()) {
          return false;
        }
        await sleep(REFRESH_LOCK_POLL_MS);
      }
      return false;
    };

    const persisted = getPersistedTokens();
    const refreshToken = persisted?.refreshToken ?? get().refreshToken;
    if (!refreshToken) return false;

    const stateRefreshToken = get().refreshToken;
    if (stateRefreshToken && refreshToken !== stateRefreshToken) {
      return syncPersistedTokens(stateRefreshToken);
    }

    refreshInFlight = (async () => {
      if (!acquireRefreshLock(refreshToken)) {
        return waitForOtherTabRefresh(refreshToken);
      }

      try {
        const tokens = await refreshTokens(refreshToken);
        const user = await fetchCurrentUser(tokens.access_token);
        persistTokens(tokens.access_token, tokens.refresh_token);
        set({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          user,
          isAuthenticated: true,
        });
        return true;
      } catch {
        if (await syncPersistedTokens(refreshToken)) return true;
        clearPersistedTokens();
        clearAllListFilters();
        set({ accessToken: null, refreshToken: null, user: null, isAuthenticated: false });
        return false;
      } finally {
        releaseRefreshLock();
        refreshInFlight = null;
      }
    })();

    return refreshInFlight;
  },

  refreshUser: async () => {
    const { accessToken } = get();
    if (!accessToken) return;
    const user = await fetchCurrentUser(accessToken);
    set({ user });
  },

  setTokens: async (accessToken, refreshToken) => {
    const user = await fetchCurrentUser(accessToken);
    persistTokens(accessToken, refreshToken);
    set({ accessToken, refreshToken, user, isAuthenticated: true });
  },
}));
